/**
 * Income/Expense API Client — Epic 6
 * Follows brokerApi.ts pattern for API calls
 */

import { authenticatedFetch } from './authApi'

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const detail = err.detail
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join('; ') || `HTTP ${res.status}`
          : `HTTP ${res.status}`
    throw new Error(message)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type IncomeType = 'salary' | 'business' | 'investment' | 'rental' | 'other'
export type ExpenseType = 'rent_mortgage' | 'utilities' | 'loans' | 'credit_cards' | 'living' | 'other'
export type Frequency = 'weekly' | 'fortnightly' | 'monthly' | 'annual'

export interface IncomeEntry {
  id: string
  income_type: IncomeType
  amount: number
  frequency: Frequency
  employer_name?: string
  start_date?: string
  created_at: string
  updated_at: string
}

export interface ExpenseEntry {
  id: string
  expense_type: ExpenseType
  amount: number
  frequency: Frequency
  description?: string
  created_at: string
  updated_at: string
}

export interface CashFlowSummary {
  total_monthly_income: number
  total_monthly_expenses: number
  net_monthly_cashflow: number
  income_count: number
  expense_count: number
}

// ── Income CRUD ───────────────────────────────────────────────────────────────

export async function createIncome(
  token: string,
  data: Omit<IncomeEntry, 'id' | 'created_at' | 'updated_at'>,
): Promise<IncomeEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/income`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<IncomeEntry>(res)
}

export async function getIncomes(token: string): Promise<IncomeEntry[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/income`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ income: IncomeEntry[]; count: number }>(res)
  return Array.isArray(data.income) ? data.income : []
}

export async function updateIncome(
  token: string,
  id: string,
  data: Partial<IncomeEntry>,
): Promise<IncomeEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/income/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<IncomeEntry>(res)
}

export async function deleteIncome(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/income/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── Expense CRUD ──────────────────────────────────────────────────────────────

export async function createExpense(
  token: string,
  data: Omit<ExpenseEntry, 'id' | 'created_at' | 'updated_at'>,
): Promise<ExpenseEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/expenses`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<ExpenseEntry>(res)
}

export async function getExpenses(token: string): Promise<ExpenseEntry[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/expenses`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ expenses: ExpenseEntry[]; count: number }>(res)
  return Array.isArray(data.expenses) ? data.expenses : []
}

export async function updateExpense(
  token: string,
  id: string,
  data: Partial<ExpenseEntry>,
): Promise<ExpenseEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/expenses/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<ExpenseEntry>(res)
}

export async function deleteExpense(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/expenses/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── Cash Flow Summary ─────────────────────────────────────────────────────────

export async function getCashFlow(token: string): Promise<CashFlowSummary> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/cashflow`, {
    headers: authHeaders(token),
  })
  return handleResponse<CashFlowSummary>(res)
}

// ── Liability & Asset Types ──────────────────────────────────────────────────

export type LiabilityType = 'credit_card' | 'personal_loan' | 'car_loan' | 'hecs_help' | 'mortgage' | 'bnpl' | 'other'
export type AssetType = 'real_estate' | 'savings' | 'term_deposit' | 'shares' | 'superannuation' | 'vehicle' | 'other'

export interface LiabilityEntry {
  id: string
  liability_type: LiabilityType
  lender_name?: string
  outstanding_balance: number
  credit_limit?: number
  monthly_repayment?: number
  interest_rate?: number
  remaining_term_months?: number
  description?: string
  created_at: string
  updated_at: string
}

export interface AssetEntry {
  id: string
  asset_type: AssetType
  description?: string
  estimated_value: number
  institution_name?: string
  address?: string
  created_at: string
  updated_at: string
}

export interface BrokerFinancialSummary {
  shared: boolean
  income: IncomeEntry[]
  expenses: ExpenseEntry[]
  liabilities: LiabilityEntry[]
  assets: AssetEntry[]
  summary: {
    total_monthly_income: number
    total_monthly_expenses: number
    total_monthly_liabilities: number
    total_liability_balance: number
    total_assets: number
    net_monthly_cashflow: number
    net_worth: number
    income_count: number
    expense_count: number
    liability_count: number
    asset_count: number
  }
}

// ── Liability CRUD ───────────────────────────────────────────────────────────

export async function createLiability(
  token: string,
  data: Omit<LiabilityEntry, 'id' | 'created_at' | 'updated_at'>,
): Promise<LiabilityEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/liabilities`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<LiabilityEntry>(res)
}

export async function getLiabilities(token: string): Promise<LiabilityEntry[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/liabilities`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ liabilities: LiabilityEntry[]; count: number }>(res)
  return Array.isArray(data.liabilities) ? data.liabilities : []
}

export async function updateLiability(
  token: string,
  id: string,
  data: Partial<LiabilityEntry>,
): Promise<LiabilityEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/liabilities/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<LiabilityEntry>(res)
}

export async function deleteLiability(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/liabilities/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── Asset CRUD ───────────────────────────────────────────────────────────────

export async function createAsset(
  token: string,
  data: Omit<AssetEntry, 'id' | 'created_at' | 'updated_at'>,
): Promise<AssetEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/assets`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<AssetEntry>(res)
}

export async function getAssets(token: string): Promise<AssetEntry[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/assets`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ assets: AssetEntry[]; count: number }>(res)
  return Array.isArray(data.assets) ? data.assets : []
}

export async function updateAsset(
  token: string,
  id: string,
  data: Partial<AssetEntry>,
): Promise<AssetEntry> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/assets/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })
  return handleResponse<AssetEntry>(res)
}

export async function deleteAsset(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/assets/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── Financial Sharing ────────────────────────────────────────────────────────

export async function shareFinancials(
  token: string,
  shared: boolean,
): Promise<{ shared: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/financials/share`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ shared }),
  })
  return handleResponse<{ shared: boolean }>(res)
}

export async function getFinancialsSharedStatus(
  token: string,
): Promise<{ shared: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/profile/financials/shared-status`, {
    headers: authHeaders(token),
  })
  return handleResponse<{ shared: boolean }>(res)
}

// ── Broker Financial View ────────────────────────────────────────────────────

export async function getBrokerClientFinancials(
  token: string,
  customerId: string,
): Promise<BrokerFinancialSummary> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/broker/clients/${customerId}/financials`, {
    headers: authHeaders(token),
  })
  return handleResponse<BrokerFinancialSummary>(res)
}
